import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoSearchSharp, IoAddOutline } from 'react-icons/io5';
import { FiFilter } from 'react-icons/fi';
import { useQuery, QueryObserverResult } from 'react-query';
import { color, CredusButton, CredusDropDown, CredusText } from 'ui';

import TestDataTable from './tables/TestDataTable';
import TestActionSuccess from './modals/TestActionSuccess';
import ComponentLoader from '../../ComponentLoader';
import CreateFirstTest from './CreateFirstTest';
import { Checkbox } from 'antd';
import AssignUserModal from './modals/AssignUserModal';
import { PublishedTests } from '../../../../contexts/TestContext';
import testApi from '../../../../api/test';

export interface TestDataProps {
  setCreateTest: React.Dispatch<React.SetStateAction<boolean>>;
}

export type Refetch = () => Promise<QueryObserverResult<unknown>>;

const TestData = ({ setCreateTest }: TestDataProps) => {
  const [testData, setTestData] = useState<PublishedTests[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [activeTestId, setActiveTestId] = useState(0);
  const [testIds, setTestIds] = useState<number[]>([]);
  const [assignUser, setAssignUser] = useState(false);
  const [isBatchAssign, setIsBatchAssign] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showActionSuccess, setShowActionSuccess] = useState(false);
  const [sortBy, setSortBy] = useState<"ASC"|"DESC">('DESC')
  const statusOptions = [
    'All',
    'Active',
    'Assigned',
    ' Completed',
    'Suspended',
  ];
  const [status, setStatus] = useState('');
  const { data, isLoading, refetch } = useQuery(
    ['publishedTests', status],
    () => testApi.getPublishedTests(1, sortBy, status)
  );

  const handleStatusChange = (status: string) => {
    if (status === 'All' || status === '') {
      setStatus('');
    } else setStatus(status);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);

    const filteredData = data?.data.filter((test: PublishedTests) => {
      return test.subject.toLowerCase().includes(e.target.value.toLowerCase());
    });
    if (filteredData?.length === 0 || !filteredData) {
      return setTestData(data?.data ?? []);
    }
    setTestData(filteredData);
  };

  useEffect(() => {
    if (data?.data && Array.isArray(data?.data)) {
      setTestData(data.data);
    }
  }, [data?.data]);

  const handleAssign = () => {
    setAssignUser(true);
    setSuccessMessage(
      'All tests selected have been batch assigned to a period successfully '
    );
  };

  const handleCreate = () => {
    setCreateTest(true);
  };

  return (
    <>
      {isLoading ? (
        <>
          <ComponentLoader loaderText="getting test data" />
        </>
      ) : data?.data && data?.data?.length !== 0 ? (
        <div>
          {assignUser && (
            <AssignUserModal
              assignUser={assignUser}
              setAssignUser={setAssignUser}
              testId={activeTestId}
              testIds={testIds}
              setShowActionSuccess={setShowActionSuccess}
              isBatchAssign={isBatchAssign}
            />
          )}
          {showActionSuccess && (
            <TestActionSuccess
              showSuccess={showActionSuccess}
              successMessage={successMessage}
              setShowSuccess={setShowActionSuccess}
              refetch={refetch}
            />
          )}
          <TopDiv>
            <SearchDiv>
              <Input
                placeholder="Try searching for information"
                value={searchTerm}
                onChange={handleSearch}
              />
              <IoSearchSharp color="#C3CAD9" />
            </SearchDiv>
            <FilterDiv>
              <CredusText weight="medium" size="xs">
                Filter by
              </CredusText>
              <Filter>
                <Div onClick={() => setShowFilter((prev) => !prev)}>
                  <CredusText weight="medium" size="xs">
                    {status ? status : 'All'}
                  </CredusText>
                  <FiFilter color={'#E0DEF7'} />
                </Div>
                {showFilter && (
                  <CredusDropDown
                    items={statusOptions.map((status) => (
                      <Item key={status} onClick={() => handleStatusChange(status)}>
                        {/* <Checkbox /> */}
                        <CredusText weight="medium" size="xs">
                          {status}
                        </CredusText>
                      </Item>
                    ))}
                  />
                )}
              </Filter>
              <CredusButton
                outlined
                onClick={handleAssign}
                disabled={!isBatchAssign}
              >
                Batch Assign
              </CredusButton>
              <CredusButton
                preffix={<IoAddOutline size={'25px'} />}
                onClick={handleCreate}
              >
                Create Test
              </CredusButton>
            </FilterDiv>
          </TopDiv>

          <TestDataTable
            testData={testData ? testData : data?.data}
            refetch={refetch}
            setSuccessMessage={setSuccessMessage}
            setShowActionSuccess={setShowActionSuccess}
            setIsBatchAssign={setIsBatchAssign}
            activeTestId={activeTestId}
            setActiveTestId={setActiveTestId}
            setAssignUser={setAssignUser}
            testIds={testIds}
            setTestIds={setTestIds}
          />
        </div>
      ) : (
        <CreateFirstTest setCreateTest={setCreateTest} />
      )}
    </>
  );
};

export default TestData;

const TopDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 2rem;
`;
const SearchDiv = styled.div`
  width: 326px;
  height: 36px;
  background: ${color.white};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  box-shadow: 0px 1.44px 3.6px rgba(38, 51, 77, 0.03);
`;

const Input = styled.input`
  outline: none;
  border: none;
  width: 90%;
  &::placeholder {
    color: #adb8cc;
  }
`;

const Item = styled.div`
  display: flex;
  gap: 1rem;
`;

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;
const Filter = styled.div`
  position: relative;
  min-width: 160px;
`;

const Div = styled.div`
  width: 100%;
  height: 36px;
  background: ${color.white};
  border-radius: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  border: 1px solid #e0def7;
  gap: 0.5rem;
  padding: 0 1rem;
  box-shadow: 0px 1.44px 3.6px rgba(38, 51, 77, 0.03);
  position: relative;
`;
